/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from "react";
import { getDevicePixelRatio } from "dc-preview-utils/src/Utils/DeviceUtils";
import PageViewCss from "./PageView.css";
import PageTileContainer from "containers/document/page/PageTileContainer";
import ImgBitmap from "./ImgBitmap";
import { isHighContrastEnabled } from "selectors/highContrastSelectors";
import { getTotalPages } from "selectors/documentSelectors";
import * as stores from "stores";
import classNames from "classnames";
import * as constants from "constants/constants";
import { thumbnailOrientationState } from "constants/enums";
import {
  getProgressiveCanvasDetails,
  getDynamicCanvasContainerId,
  getPlaceholderCanvasId,
  getProgressiveCanvasId
} from "utils/PageUtils";
import BottomBannerContainer from "../../../containers/bottomBoard/BottomBannerContainer";
import { triggerDocumentViewStateEvent } from "publicApis/Events/events";

class PageView extends Component {
  constructor(props) {
    super(props);
    this.handlePageClick = this.handlePageClick.bind(this);
    this.handleImgBitmapLoaded = this.handleImgBitmapLoaded.bind(this);
    this.state = {
      showPageCanvas: true,
      showDynamicallyAddedCanvas: true,
      loadPageAccComponent: false
    };
    this.dynamicCanvasPlaceholderRef = React.createRef();
    this.lastPageIndex = getTotalPages(stores) - 1;
  }

  handleImgBitmapLoaded() {
    this.setState({ showDynamicallyAddedCanvas: false, showPageCanvas: false });
    if (!window.isTimeToViewFirstPageRecorded) {
      this.props.onFirstPageRendered && this.props.onFirstPageRendered();
      window.isTimeToViewFirstPageRecorded = true;
    }

    setTimeout(() => {
      this.setState({ loadPageAccComponent: true });
    }, 0);
  }

  componentDidMount() {
    triggerDocumentViewStateEvent(stores);
    if (this.props.setPageHandle) {
      this.props.setPageHandle(this.elementHandle);
    }

    if (this.props.onMount) {
      this.props.onMount();
    }

    const dynamicProgressiveCanvas = document.getElementById(getDynamicCanvasContainerId(this.props.pageIndex));
    if (this.state.showDynamicallyAddedCanvas && dynamicProgressiveCanvas) {
      // If we are using dynamic canvas, do not use page canvas
      const progressiveCanvasPlaceholder = this.dynamicCanvasPlaceholderRef.current;
      if (progressiveCanvasPlaceholder) {
        this.setState({ showPageCanvas: false });
        dynamicProgressiveCanvas.style.display = "block";
        progressiveCanvasPlaceholder.insertAdjacentElement("afterend", dynamicProgressiveCanvas);
      } else {
        dynamicProgressiveCanvas.remove();
      }
    }
  }

  componentWillUnmount() {
    if (this.props.onUnMount) {
      this.props.onUnMount();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.setPageHandle) {
      this.props.setPageHandle(this.elementHandle);
    }
    triggerDocumentViewStateEvent(stores);
    // Remove progressive canvas if page is reloaded/loaded due to caching/remounting and it is no longer a progressive page after reloading
    if (
      this.props.isPageLoaded &&
      !this.props.isProgressivePage &&
      (this.state.showPageCanvas || this.state.showDynamicallyAddedCanvas)
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ showPageCanvas: false, showDynamicallyAddedCanvas: false });
    }
    if (prevProps.isTwoColumn !== this.props.isTwoColumn && this.props.setPageHandle) {
      this.props.setPageHandle(this.elementHandle);
    }
  }

  handlePageClick() {
    this.props.hideStickyToolOnTap && this.props.toggleStickyToolPanel();
    if (this.props.onPageClick) {
      this.props.onPageClick(this.props.pageIndex);
    }
  }

  isRotated() {
    return this.props.viewRotationAngle === 90 || this.props.viewRotationAngle === 270;
  }

  getBitmapHeight() {
    if (this.props.bitmapHeight) {
      return this.props.bitmapHeight;
    }
    return !this.isRotated() ? this.props.height : this.props.width;
  }

  getBitmapWidth() {
    if (this.props.bitmapWidth) {
      return this.props.bitmapWidth;
    }
    return !this.isRotated() ? this.props.width : this.props.height;
  }

  getRotationStyle() {
    const devicePixelRatio = getDevicePixelRatio(),
      epsilon = 0.01;
    let translateX = this.getBitmapWidth() / 2,
      translateY = this.getBitmapHeight() / 2;
    // These shifts can be in multiples of 0.5px, so they need to be rounded
    // according to devicePixelRatio, to prevent blur due to subpixel rendering
    if (Math.abs(devicePixelRatio - 1) < epsilon) {
      // Requires multiple of 1px, so round to nearest pixel
      translateX = Math.round(translateX);
      translateY = Math.round(translateY);
    } else if (Math.abs(devicePixelRatio - 1.5) < epsilon) {
      // Requires multiples of 2px, so round to nearest even pixel
      translateX = 2 * Math.round(translateX / 2);
      translateY = 2 * Math.round(translateY / 2);
    } // We don't handle other special cases
    let rotateString = `rotate(${this.props.viewRotationAngle}deg)`;
    if (this.props.useModernViewer && this.props.thumbnailOrientation === thumbnailOrientationState.LANDSCAPE) {
      rotateString = `rotate(${this.props.viewRotationAngle - 90}deg)`;
    }
    const rotateTransform = `translate(-${translateX}px, -${translateY}px) ${rotateString}`;
    return { translateX, translateY, rotateTransform };
  }

  _createTiles({ pageTilesCount }) {
    let tileArr = [];
    for (let i = 0; i < pageTilesCount; i++) {
      tileArr.push(<PageTileContainer key={i} tileIndex={i} pageIndex={this.props.pageIndex} />);
    }
    return tileArr;
  }

  _styleProgressiveCanvas(styles) {
    const { canvas } = getProgressiveCanvasDetails(
      this.props.pageIndex,
      constants.PROGRESSIVE_RENDERING_WORKFLOWS.PAGE_RENDERING
    );

    if (canvas) {
      for (const prop in styles) {
        if (prop === "height" || prop === "width") {
          canvas.style[prop] = `${styles[prop]}px`;
        } else {
          canvas.style[prop] = styles[prop];
        }
      }
    }
  }

  render() {
    const {
      height,
      width,
      pageBitmap,
      pageIndex,
      pageMarginBottom,
      shouldRenderPageShadows,
      PageAccessibilityComponent,
      viewportWidth,
      shouldShowThumbnailLoader,
      thumbnailOrientation,
      useModernViewer,
      isProgressiveRenderingEnabled,
      itemType
    } = this.props;
    const { showPageCanvas, showDynamicallyAddedCanvas } = this.state;

    const bitmapHeight = this.getBitmapHeight();
    const bitmapWidth = this.getBitmapWidth();

    let boxShadow = "";

    if (shouldRenderPageShadows) {
      boxShadow = "0px 0px 10px #000";
    }

    const { margins } = this.props;
    let leftMargin = margins.left === "auto" ? margins.left : `${margins.left}px`;
    let rightMargin = margins.right === "auto" ? margins.right : `${margins.right}px`;

    const style = {
      height,
      width,
      marginBottom: `${pageMarginBottom}px`,
      boxShadow,
      ...(this.props.invertColors && { backgroundColor: "rgba(50, 50, 50, 0.8)" })
    };

    let topStyle = {
      display: "flex",
      flexDirection: "column"
    };

    if (this.props.isTwoColumn) {
      if (this.props.isLeftPageIndex(pageIndex)) {
        topStyle.alignItems = "flex-end";
        style.marginRight = `${constants.TWO_COLUMN_MARGIN}px`;
        style.marginLeft = `${constants.PAGES_VIEW_MARGIN_LEFT}px`;
      } else {
        style.marginRight = `${constants.PAGES_VIEW_MARGIN_RIGHT}px`;
      }
      topStyle.justifyContent = "center";
    }

    if (leftMargin !== "auto" && rightMargin !== "auto") {
      let leftValue, remainingWidth;
      remainingWidth = viewportWidth - margins.left - margins.right;

      leftValue = margins.left;
      if (remainingWidth > width) {
        leftValue += (remainingWidth - width) / 2;
      }

      Object.assign(style, {
        marginLeft: `${leftValue}px`,
        marginRight: rightMargin
      });
    }

    // If any event handlers are passed as props, they will be applied, else,
    // none will be applied.
    // If you have an overlay on top of the page, you would probably want to
    // have event listeners on the overlay rather than the page.
    const { onMouseMove = null } = this.props;

    let overlays = null;
    if (this.props.overlays && this.props.overlays.length) {
      overlays = this.props.overlays.map((Overlay, index) => (
        <Overlay
          key={index}
          {...{ height, width, pageIndex, shouldSkipMouseEnterLeave: this.props.loadNonPDFPageCanvas }}
        />
      ));
    }

    const propsStyle = this.props.style ? this.props.style : {};

    // Align according to devicepixelratio
    const { translateX, translateY, rotateTransform } = this.getRotationStyle();

    let imageStyle = {
      width: bitmapWidth,
      height: bitmapHeight,
      display: pageBitmap ? "block" : "none",
      ...(this.props.invertColors && { filter: "invert(1) grayscale(1)" })
    };
    const tilesStyle = {
      position: "absolute",
      width: bitmapWidth,
      height: bitmapHeight,
      top: "50%",
      transform: rotateTransform,
      left: "50%",
      ...(this.props.invertColors && { filter: "invert(1) grayscale(1)" })
    };
    if (this.props.viewRotationAngle) {
      imageStyle.top = "50%";
      imageStyle.transform = rotateTransform;
      imageStyle.transformOrigin = `${translateX}px ${translateY}px`;
      imageStyle.left = "50%";
    } else {
      imageStyle.inset = "0px";
    }

    const tilesView = this.props.pageTilesCount ? <div style={tilesStyle}>{this._createTiles(this.props)}</div> : null;
    const pageImageView = (
      <ImgBitmap
        id={`img-bitmap-index-${pageIndex}`}
        style={imageStyle}
        src={pageBitmap}
        onImageLoaded={this.handleImgBitmapLoaded}
      />
    );

    let rootClass = PageViewCss.PageView;
    if (shouldShowThumbnailLoader) {
      rootClass = classNames(rootClass, PageViewCss.emptySkeleton);
    }

    if (useModernViewer && !this.props.viewRotationAngle) {
      if (thumbnailOrientation === thumbnailOrientationState.LANDSCAPE) imageStyle.transform = "rotate(-90deg)";
    }

    if (isHighContrastEnabled(stores)) {
      style.border = "2px solid ButtonText";
      // adding 4px in height to accomodate top and bottom border thickness
      style.height += 4;
      propsStyle.height += 4;
    }

    if (this.props.useSinglePageCSS) {
      style.marginTop = "auto";
      style.marginBottom = "auto";
      topStyle.position = "absolute";
      topStyle.left = 0;
      topStyle.top = 0;
      topStyle.height = "inherit";
      topStyle.flexDirection = "unset";
    }

    const progressiveCanvasContainerStyles = {
      position: "absolute",
      top: imageStyle.top,
      left: imageStyle.left,
      transform: this.props.viewRotationAngle ? `${rotateTransform}` : null
    };

    const progressiveCanvasStyles = {
      height: imageStyle.height,
      width: imageStyle.width,
      display: "block",
      ...(this.props.invertColors && { filter: "invert(1) grayscale(1)" })
    };

    this._styleProgressiveCanvas(progressiveCanvasStyles);

    const canvasId = getProgressiveCanvasId(pageIndex, constants.PROGRESSIVE_RENDERING_WORKFLOWS.PAGE_RENDERING);

    const placeholderCanvasId = getPlaceholderCanvasId(pageIndex);
    const isLastPage = this.props.pageIndex === this.lastPageIndex;

    if (isLastPage && stores.bottomBoard.showBanner) {
      propsStyle.height += stores.bottomBoard.boundingBox.height;
    }

    return (
      <div style={{ ...topStyle, ...propsStyle }} data-index={pageIndex}>
        <div
          className={classNames(rootClass, {
            [PageViewCss.PageViewAutoMargin]: !this.props.isTwoColumn
          })}
          style={style}
          onMouseMove={onMouseMove}
          ref={elementHandle => {
            this.elementHandle = elementHandle;
            if (this.props.enableAccessibilityFixes && this.props.refCallback instanceof Function) {
              this.props.refCallback(elementHandle);
            }
          }}
          onClick={this.handlePageClick}
          {...(this.props.enableAccessibilityFixes && { tabIndex: -1 })}
        >
          {pageImageView}
          {tilesView}
          {isProgressiveRenderingEnabled ? (
            <div style={{ ...progressiveCanvasContainerStyles }}>
              {showDynamicallyAddedCanvas && (
                <div>
                  <div id={placeholderCanvasId} style={{ display: "none" }} ref={this.dynamicCanvasPlaceholderRef} />
                </div>
              )}
              {itemType === "page" && showPageCanvas && <canvas id={canvasId} aria-hidden="true" />}
            </div>
          ) : null}

          {this.state.loadPageAccComponent && PageAccessibilityComponent && (
            <PageAccessibilityComponent pageIndex={pageIndex} height={height} width={width} />
          )}
          {overlays}
        </div>
        {isLastPage && <BottomBannerContainer />}
      </div>
    );
  }
}

PageView.defaultProps = {
  margins: {
    left: "auto",
    right: "auto"
  },
  shouldRenderPageShadows: false,
  PageAccessibilityComponent: null,
  itemType: "page"
};

export { PageView };
